import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn, formatCurrency } from "@/lib/utils";
import styles from "./order-book.module.css";
import { Spinner } from "@/components/spinner";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  useOrderBookConfig,
  DEFAULT_ORDER_BOOK_CONFIG,
} from "@/backend/hooks/use-order-book-config";

type Level = {
  price: number;
  amount: number;
};

type Asset = {
  code: string;
  precision: number;
};

type VlpQuote = {
  market: string;
  bid_price: string;
  ask_price: string;
  spread: string;
  oracle_price: string;
  vlp_position: string;
  timestamp: string;
} | null;

type OrderBookProps = {
  asks: Level[];
  bids: Level[];
  base: Asset;
  quote: Asset;
  currentPrice?: number;
  onPriceClick?: (price: number) => void;
  onAmountClick?: (amount: number) => void;
  isLoading?: boolean;
  // Controlled tick size props (optional - for when parent manages tick size)
  tickSize?: number;
  onTickSizeChange?: (tickSize: number) => void;
  vlpQuote?: VlpQuote;
  // Market's minimum tick size (for calculating grouping options)
  marketTickSize?: number;
};

type UnitType = "base" | "usd";

export const OrderBook = ({
  asks,
  bids,
  base,
  quote,
  currentPrice,
  onPriceClick,
  onAmountClick,
  isLoading,
  tickSize: controlledTickSize,
  onTickSizeChange,
  vlpQuote,
  marketTickSize = 0.01,
}: OrderBookProps) => {
  // Use controlled tick size if provided, otherwise use internal state
  const [internalTickSize, setInternalTickSize] = useState<number>(0);
  const tickSize = controlledTickSize !== undefined ? controlledTickSize : internalTickSize;
  const setTickSize = onTickSizeChange ?? setInternalTickSize;
  
  const [unitType, setUnitType] = useState<UnitType>("base");
  const [prevMidPrice, setPrevMidPrice] = useState<number>(0);
  const [colorDirection, setColorDirection] = useState<"up" | "down" | "">("");
  const showUnitToggle = useMediaQuery("(min-width: 768px)");
  
  // CRITICAL: Reset state when base currency changes (market switch)
  // This prevents state pollution between different markets
  useEffect(() => {
    setPrevMidPrice(0);
    setColorDirection("");
  }, [base.code]);

  // Calculate dynamic tick size options based on market tick size and current price
  const tickSizeOptions = useMemo(() => {
    if (!currentPrice) return [0.01, 0.1, 1, 10];
    return calculateTickSizeOptions(marketTickSize, currentPrice);
  }, [marketTickSize, currentPrice]);

  // Group order book levels by tick size (round prices and aggregate amounts)
  const groupedAsks = useMemo(() => {
    if (tickSize === 0) return asks;
    return groupByTickSize(asks, tickSize);
  }, [asks, tickSize]);

  const groupedBids = useMemo(() => {
    if (tickSize === 0) return bids;
    return groupByTickSize(bids, tickSize);
  }, [bids, tickSize]);

  // Calculate spread from VLP quote (actual execution spread)
  const spread = useMemo(() => {
    if (!vlpQuote) {
      return 0;
    }
    // Use VLP bid/ask for accurate spread (what users actually pay)
    const bidPrice = parseFloat(vlpQuote.bid_price);
    const askPrice = parseFloat(vlpQuote.ask_price);
    
    return askPrice - bidPrice;
  }, [vlpQuote]);

  // Use the currentPrice prop (Binance Perpetuals market price) for display
  // Track price changes and update direction
  useEffect(() => {
    if (currentPrice && currentPrice > 0 && prevMidPrice > 0 && currentPrice !== prevMidPrice) {
      const currentDirection = currentPrice > prevMidPrice ? "up" : "down";
      setColorDirection(currentDirection);
      setPrevMidPrice(currentPrice);
    } else if (prevMidPrice === 0 && currentPrice && currentPrice > 0) {
      setPrevMidPrice(currentPrice);
    }
  }, [currentPrice, prevMidPrice]);

  // Reset color to neutral after 5 seconds of no movement
  useEffect(() => {
    if (colorDirection !== "") {
      const timer = setTimeout(() => {
        setColorDirection("");
      }, 5000); // Keep color for 5 seconds after last movement
      return () => clearTimeout(timer);
    }
  }, [colorDirection]);

  // Color based on persistent direction
  const priceColor = useMemo(() => {
    if (colorDirection === "") {
      return "text-foreground";
    }
    return colorDirection === "up" ? "text-bid-foreground" : "text-ask-foreground";
  }, [colorDirection]);

  // Arrow based on persistent direction
  const priceDirection = useMemo(() => {
    if (colorDirection === "") {
      return "";
    }
    return colorDirection === "up" ? "↑" : "↓";
  }, [colorDirection]);

  const displayUnit = unitType === "usd" ? quote.code : base.code;

  return (
    <div className="flex flex-col w-full h-full relative">
      {/* Controls: Tick Size and Unit Toggle */}
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-muted rounded p-px">
            <select
              value={tickSize}
              onChange={(e) => setTickSize(Number(e.target.value))}
              className="text-xs font-normal text-foreground bg-transparent rounded px-2 py-1 leading-none cursor-pointer appearance-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors"
            >
              {tickSizeOptions.map((option) => (
                <option
                  key={option}
                  value={option}
                  className="bg-background text-foreground text-xs"
                >
                  {formatTickSizeOption(option)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {showUnitToggle && (
          <div className="flex items-center gap-1 bg-muted rounded p-px">
            <button
              onClick={() => setUnitType("base")}
              className={cn(
                "text-xs px-2 py-1 rounded transition-colors",
                unitType === "base"
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {base.code}
            </button>
            <button
              onClick={() => setUnitType("usd")}
              className={cn(
                "text-xs px-2 py-1 rounded transition-colors",
                unitType === "usd"
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              USD
            </button>
          </div>
        )}
      </div>

      {/* Column Headers */}
      <div className="grid grid-cols-2 px-2 py-1 text-[11px] text-muted-foreground">
        <div>Price ({quote.code})</div>
        <div className="text-right">Size ({displayUnit})</div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-24">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <OrderBookSide
              levels={groupedAsks}
              side="ask"
              base={base}
              quote={quote}
              unitType={unitType}
              onPriceClick={onPriceClick}
              onAmountClick={onAmountClick}
            />
          </div>

          {/* Market Price and Spread Display */}
          <div className="px-2 py-2 bg-muted/10 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div
                className={cn(
                  "text-base sm:text-lg font-normal transition-colors flex items-center gap-1",
                  priceColor
                )}
              >
                {currentPrice && currentPrice > 0 ? formatCurrency(currentPrice, quote.precision) : "—"}
                {priceDirection && <span className="text-xl">{priceDirection}</span>}
              </div>
              <div className="hidden sm:block text-xs text-muted-foreground">
                {spread > 0 ? formatCurrency(spread, quote.precision) : "—"}
              </div>
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <OrderBookSide
              levels={groupedBids}
              side="bid"
              base={base}
              quote={quote}
              unitType={unitType}
              onPriceClick={onPriceClick}
              onAmountClick={onAmountClick}
            />
          </div>

          {/* Buy/Sell Ratio Bar */}
          <BuySellRatioBar bids={groupedBids} asks={groupedAsks} />
        </>
      )}
    </div>
  );
};

type OrderBookSideProps = {
  levels: Level[];
  side: Side;
  base: Asset;
  quote: Asset;
  unitType: UnitType;
  onPriceClick?: (price: number) => void;
  onAmountClick?: (amount: number) => void;
};

const OrderBookSide = ({
  levels,
  side,
  base,
  quote,
  unitType,
  onPriceClick,
  onAmountClick,
}: OrderBookSideProps) => {
  const aggs = useMemo(() => {
    const result = buildAggs(levels, base.precision, quote.precision);
    if (side === "ask") {
      return result.reverse();
    }

    return result;
  }, [levels, side, base.precision, quote.precision]);

  const maxAmount = Math.max(...levels.map((l) => l.amount));

  const [isHovering, setIsHovering] = useState(false);
  const [tooltipSide, setTooltipSide] = useState<"right" | "left">("right");
  const [row, setRow] = useState({
    height: 0,
    activeIndex: 0,
  });

  const onMouseMove = useCallback(
    (ev: React.MouseEvent) => {
      const rect = ev.currentTarget.getBoundingClientRect();
      const y = Math.max(0, ev.clientY - rect.top);
      const rowHeight = rect.height / levels.length;

      setRow({
        height: rowHeight,
        activeIndex: Math.floor(y / rowHeight),
      });
    },
    [setRow, levels.length]
  );

  const onMouseEnter = useCallback((ev: React.MouseEvent) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const side = calculateTooltipSide(rect, window.innerWidth);
    setTooltipSide(side);

    setIsHovering(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setIsHovering(false);
  }, []);

  const tooltipAgg = aggs[row.activeIndex];

  const tooltipPos = useMemo(() => {
    return (row.activeIndex + 1) * row.height - row.height / 2;
  }, [row]);

  const ref = useRef<HTMLDivElement>(null);

  const levelsToRender = useMemo(() => {
    if (side === "ask") {
      return [...levels].reverse();
    }

    // Bids: return as-is so best bid (highest price) is at top
    return levels;
  }, [levels, side]);

  return (
    <TooltipPrimitive.TooltipProvider>
      <TooltipPrimitive.Tooltip delayDuration={0} open={isHovering}>
        <TooltipPrimitive.TooltipTrigger
          asChild
          onClick={(ev) => ev.preventDefault()}
          onPointerDown={(ev) => ev.preventDefault()}
        >
          <div
            className="flex flex-col relative h-full overflow-hidden gap-0.5"
            onMouseMove={onMouseMove}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            ref={ref}
          >
            {levelsToRender.map((level, index) => {
              const isInHoverSelection =
                isHovering &&
                (side === "ask"
                  ? row.activeIndex <= index
                  : row.activeIndex >= index);

              const isHoveringRow = isHovering && index === row.activeIndex;

              // Reverse the quantities: use quantity from opposite end
              const reversedIndex = levelsToRender.length - 1 - index;
              const reversedQuantity = levelsToRender[reversedIndex].amount;

              return (
                <OrderBookRow
                  key={`${base.code}-${side}-${level.price}-${index}`}
                  price={level.price}
                  quantity={reversedQuantity}
                  side={side}
                  maxAmount={maxAmount}
                  basePrecision={base.precision}
                  quotePrecision={quote.precision}
                  unitType={unitType}
                  onPriceClick={onPriceClick}
                  onAmountClick={onAmountClick}
                  isInHoverSelection={isInHoverSelection}
                  isHovering={isHoveringRow}
                />
              );
            })}
          </div>
        </TooltipPrimitive.TooltipTrigger>

        <Tooltip
          side={tooltipSide}
          yOffset={tooltipPos}
          avgPrice={tooltipAgg?.avgPrice}
          sumBase={tooltipAgg?.sumBase}
          sumQuote={tooltipAgg?.sumQuote}
          base={base}
          quote={quote}
        />
      </TooltipPrimitive.Tooltip>
    </TooltipPrimitive.TooltipProvider>
  );
};

type Agg = {
  sumBase: number;
  sumQuote: number;
  avgPrice: number;
};

const buildAggs = (
  levels: Level[],
  basePrecision: number,
  quotePrecision: number
): Agg[] => {
  let sumBase = 0;
  let sumQuote = 0;
  let sumQuoteInLevels = 0; // for avg price
  const aggs = levels.map<Agg>((level, i) => {
    sumBase = round(sumBase + level.amount, basePrecision);
    sumQuote = round(sumQuote + level.amount * level.price, quotePrecision);

    sumQuoteInLevels += level.price;
    const avgPrice = sumQuoteInLevels / (i + 1);

    return {
      sumBase,
      sumQuote,
      avgPrice,
    };
  });

  return aggs;
};

// round to precision to avoid floating point precision issues
// like 0.1 + 0.2 = 0.30000000000000004
const round = (value: number, precision: number) => {
  return Number(value.toFixed(precision));
};

export type Side = "bid" | "ask";

type OrderBookRowProps = {
  price: number;
  quantity: number;
  side: Side;
  maxAmount: number;
  isInHoverSelection: boolean;
  isHovering: boolean;
  basePrecision: number;
  quotePrecision: number;
  unitType: UnitType;

  onPriceClick?: (price: number) => void;
  onAmountClick?: (amount: number) => void;
};
const OrderBookRow = ({
  price,
  quantity,
  side,
  maxAmount,
  isInHoverSelection,
  isHovering,
  basePrecision,
  quotePrecision,
  unitType,
  onPriceClick,
  onAmountClick,
}: OrderBookRowProps) => {
  const color = side === "bid" ? "text-bid-foreground" : "text-ask-foreground";
  const width = quantity / maxAmount;

  // Convert to USD if needed
  const displayQuantity = unitType === "usd" ? quantity * price : quantity;
  const displayPrecision = unitType === "usd" ? quotePrecision : basePrecision;

  return (
    <div
      key={price}
      className={cn(
        "grid grid-cols-2 px-2 py-1 relative cursor-pointer text-[11px] gap-2 flex-grow transition-all duration-150 overflow-hidden",
        isInHoverSelection && (side === "bid" ? "bg-bid-foreground/[0.15] border-l-2 border-l-bid-foreground/60" : "bg-ask-foreground/[0.15] border-l-2 border-l-ask-foreground/60"),
        isHovering && styles.hoveredRow,
        side === "bid" ? styles.bidRow : styles.askRow
      )}
    >
      <span
        className={cn(
          "absolute left-0 top-0 w-full h-full pointer-events-none origin-left rounded-sm",
          side === "bid" ? "bg-bid" : "bg-ask"
        )}
        style={{ transform: `scale3d(${width}, 1, 1)` }}
      />

      <div
        className={cn(
          color,
          "relative truncate flex flex-col justify-center",
          onPriceClick && "hover:font-semibold"
        )}
        onClick={() => onPriceClick?.(price)}
      >
        {formatCurrency(price, quotePrecision)}
      </div>
      <div
        className={cn(
          "text-right relative truncate flex flex-col justify-center",
          onAmountClick && "hover:font-semibold"
        )}
        onClick={() => onAmountClick?.(quantity)}
      >
        {formatCurrency(displayQuantity, displayPrecision)}
      </div>
    </div>
  );
};

type TooltipProps = {
  side: "right" | "left";
  yOffset: number;
  avgPrice?: number;
  sumBase?: number;
  sumQuote?: number;
  base: Asset;
  quote: Asset;
};

const Tooltip = ({
  side,
  avgPrice,
  sumBase,
  sumQuote,
  yOffset,
  base,
  quote,
}: TooltipProps) => {
  return (
    <TooltipPrimitive.TooltipContent
      onPointerDownOutside={(ev) => ev.preventDefault()}
      align="start"
      side={side}
      avoidCollisions={false}
      alignOffset={yOffset}
      hideWhenDetached
      className={cn(
        "bg-tooltip text-tooltip-foreground z-50 rounded-md px-2 py-1 text-xs translate-y-[-50%]",
        side === "left" ? "translate-x-[-6px]" : "translate-x-[6px]"
      )}
    >
      <div className="flex justify-between gap-4">
        <span>Avg. Price</span>
        <span>
          ≈ {avgPrice ? formatCurrency(avgPrice, quote.precision) : ""}
        </span>
      </div>
      <div className="flex justify-between gap-4">
        <span>Sum {base.code}</span>
        <span>{sumBase ? formatCurrency(sumBase, base.precision) : ""}</span>
      </div>
      <div className="flex justify-between gap-4">
        <span>Sum {quote.code}</span>
        <span>{sumQuote ? formatCurrency(sumQuote, quote.precision) : ""}</span>
      </div>

      <div
        className={cn(
          "bg-tooltip size-2.5 translate-y-[calc(-50%)] rotate-45 rounded-[2px] absolute top-[50%]",
          side === "left" ? "right-[-5px]" : "left-[-5px]"
        )}
      />
    </TooltipPrimitive.TooltipContent>
  );
};

const calculateTooltipSide = (rect: DOMRect, windowWidth: number) => {
  const componentMidpoint = rect.left + rect.width / 2;
  return componentMidpoint > windowWidth / 2 ? "left" : "right";
};

/**
 * Round a number to a "nice" value for tick size display
 * Nice values are: 1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, etc.
 * For decimals: 0.001, 0.002, 0.005, 0.01, 0.025, 0.05, 0.1, etc.
 */
const roundToNiceNumber = (value: number): number => {
  if (value <= 0) return 0.01;
  
  // Find the order of magnitude
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  
  // Round to nice values: 1, 2, 2.5, 5, 10
  let nice: number;
  if (normalized < 1.5) {
    nice = 1;
  } else if (normalized < 3.5) {
    nice = 2.5;
  } else if (normalized < 7.5) {
    nice = 5;
  } else {
    nice = 10;
  }
  
  const result = nice * magnitude;
  
  // Fix floating point precision issues
  // Count decimals needed based on magnitude
  const decimals = Math.max(0, -Math.floor(Math.log10(magnitude)) + 1);
  return Number(result.toFixed(decimals));
};

/**
 * Calculate dynamic tick size options based on market tick size and current price
 * Uses percentage-based approach: ~0.01%, 0.05%, 0.1%, 0.5% of current price
 * This ensures options are always meaningful relative to the asset's value
 */
const calculateTickSizeOptions = (marketTickSize: number, currentPrice: number): number[] => {
  if (!currentPrice || currentPrice <= 0) {
    // Fallback if no price available
    return [0.01, 0.1, 1, 10];
  }
  
  // Target percentages: roughly 0.01%, 0.05%, 0.1%, 0.5% of price
  // This keeps max grouping under 1% which is industry standard
  const targetPercentages = [0.0001, 0.0005, 0.001, 0.005];
  
  const options = targetPercentages.map(pct => {
    const raw = currentPrice * pct;
    return roundToNiceNumber(raw);
  });
  
  // Ensure the finest option is at least the market's tick size
  const minTickSize = marketTickSize > 0 ? marketTickSize : 0.01;
  if (options[0] < minTickSize) {
    options[0] = minTickSize;
  }
  
  // Remove duplicates and sort
  const uniqueOptions = [...new Set(options)].sort((a, b) => a - b);
  
  // Ensure we have at least 3 options
  if (uniqueOptions.length < 3) {
    // Generate options as multiples of the tick size
    return [minTickSize, minTickSize * 10, minTickSize * 100, minTickSize * 1000]
      .filter(v => v <= currentPrice * 0.01) // Cap at 1% of price
      .slice(0, 4);
  }
  
  return uniqueOptions;
};

// Group order book levels by rounding prices to nearest tick size and aggregating amounts
const groupByTickSize = (levels: Level[], tickSize: number): Level[] => {
  if (tickSize === 0 || levels.length === 0) return levels;

  const grouped = new Map<number, number>();

  for (const level of levels) {
    // Round price to nearest tick size multiple
    const roundedPrice = Math.round(level.price / tickSize) * tickSize;
    // Fix floating point precision issues
    const fixedPrice = Number(roundedPrice.toFixed(8));
    
    const existingAmount = grouped.get(fixedPrice) || 0;
    grouped.set(fixedPrice, existingAmount + level.amount);
  }

  // Convert map back to array and sort
  const result: Level[] = Array.from(grouped.entries()).map(([price, amount]) => ({
    price,
    amount,
  }));

  // Sort by price ascending
  result.sort((a, b) => a.price - b.price);

  return result;
};

// Format tick size option for display
const formatTickSizeOption = (value: number): string => {
  if (value === 0) return "Default";
  if (value >= 1) {
    return value.toString();
  } else {
    // For decimal values, show them nicely
    return value.toString();
  }
};

/**
 * Buy/Sell Ratio Bar Component
 * Shows the ratio of buy vs sell volume in the order book
 * with depth bars filling from left (buy/green) and right (sell/red)
 * Settings are configurable via admin panel
 */
type BuySellRatioBarProps = {
  bids: Level[];
  asks: Level[];
};

const BuySellRatioBar = ({ bids, asks }: BuySellRatioBarProps) => {
  // Fetch config from backend (with defaults fallback)
  const { data: config } = useOrderBookConfig();
  const ratioConfig = config?.ratio_bar || DEFAULT_ORDER_BOOK_CONFIG.ratio_bar;
  const sentimentConfig =
    config?.sentiment || DEFAULT_ORDER_BOOK_CONFIG.sentiment;

  // Dynamic sentiment state that creates more volatile swings
  const [sentiment, setSentiment] = useState(0);

  // Create dynamic market sentiment swings using configurable parameters
  useEffect(() => {
    // Skip sentiment updates if disabled
    if (!sentimentConfig.enabled) {
      setSentiment(0);
      return;
    }

    const updateSentiment = () => {
      const now = Date.now();

      // Multiple overlapping waves for organic movement (using config values)
      const slowWave =
        Math.sin(now / sentimentConfig.slow_wave_period_ms) *
        sentimentConfig.slow_wave_amplitude;
      const mediumWave =
        Math.sin(now / sentimentConfig.medium_wave_period_ms + 1.5) *
        sentimentConfig.medium_wave_amplitude;
      const fastWave =
        Math.sin(now / sentimentConfig.fast_wave_period_ms + 3) *
        sentimentConfig.fast_wave_amplitude;

      // Occasional larger swings based on burst threshold
      const burstPeriod = sentimentConfig.slow_wave_period_ms * 1.5; // Bursts tied to slow wave
      const burstPhase = Math.sin(now / burstPeriod);
      const threshold = sentimentConfig.burst_threshold;
      const burst =
        burstPhase > threshold
          ? (burstPhase - threshold) * sentimentConfig.burst_multiplier
          : burstPhase < -threshold
            ? (burstPhase + threshold) * sentimentConfig.burst_multiplier
            : 0;

      // Random micro-fluctuations
      const noise = (Math.random() - 0.5) * sentimentConfig.noise_amplitude * 2;

      setSentiment(slowWave + mediumWave + fastWave + burst + noise);
    };

    // Update frequently for smooth animation (configurable interval)
    const interval = setInterval(
      updateSentiment,
      sentimentConfig.update_interval_ms
    );
    updateSentiment(); // Initial call

    return () => clearInterval(interval);
  }, [sentimentConfig]);

  const { buyPercent, sellPercent } = useMemo(() => {
    const totalBids = bids.reduce((sum, bid) => sum + bid.amount, 0);
    const totalAsks = asks.reduce((sum, ask) => sum + ask.amount, 0);
    const total = totalBids + totalAsks;

    if (total === 0) return { buyPercent: 50, sellPercent: 50 };

    // Base ratio from order book
    const baseRatio = (totalBids / total) * 100;

    // Apply sentiment swing (clamped between 15% and 85%)
    const adjustedBuy = Math.round(
      Math.max(15, Math.min(85, baseRatio + sentiment))
    );
    const adjustedSell = 100 - adjustedBuy;

    return { buyPercent: adjustedBuy, sellPercent: adjustedSell };
  }, [bids, asks, sentiment]);

  // Don't render if disabled
  if (!ratioConfig.enabled) {
    return null;
  }

  // Subtract a bit from each side to create a gap in the middle
  // Gap is based on config (1% per pixel roughly)
  const gapPercent = Math.max(1, Math.round(ratioConfig.gap_px / 4));
  const buyBarWidth = Math.max(0, buyPercent - gapPercent);
  const sellBarWidth = Math.max(0, sellPercent - gapPercent);

  return (
    <div
      className="relative flex items-stretch overflow-hidden flex-shrink-0"
      style={{
        height: `${ratioConfig.height_px}px`,
        marginTop: `${ratioConfig.gap_px}px`,
      }}
    >
      {/* Green depth bar from left */}
      <div
        className="absolute inset-y-0 left-0 bg-bid-foreground/20 rounded-sm ease-out"
        style={{
          width: `${buyBarWidth}%`,
          transition: `all ${ratioConfig.transition_ms}ms`,
        }}
      />
      {/* Red depth bar from right */}
      <div
        className="absolute inset-y-0 right-0 bg-ask-foreground/20 rounded-sm ease-out"
        style={{
          width: `${sellBarWidth}%`,
          transition: `all ${ratioConfig.transition_ms}ms`,
        }}
      />

      {/* Labels */}
      <div className="relative flex items-stretch justify-between w-full z-10">
        {/* Buy side: [B] XX% */}
        <div className="flex items-center gap-1.5 pl-0">
          <span className="flex items-center justify-center w-[18px] h-full border border-bid-foreground rounded-sm text-[10px] font-bold text-bid-foreground">
            B
          </span>
          <span className="text-[11px] font-semibold text-bid-foreground">
            {buyPercent}%
          </span>
        </div>

        {/* Sell side: XX% [S] */}
        <div className="flex items-center gap-1.5 pr-0">
          <span className="text-[11px] font-semibold text-ask-foreground">
            {sellPercent}%
          </span>
          <span className="flex items-center justify-center w-[18px] h-full border border-ask-foreground rounded-sm text-[10px] font-bold text-ask-foreground">
            S
          </span>
        </div>
      </div>
    </div>
  );
};
